import { clsx } from 'clsx'
import parse from 'html-react-parser'
import { type ComponentPropsWithRef, type ReactElement } from 'react'

import { IconSprites } from '@constants/enums/sprites'

import { Column } from '@components/ui-core/column/column'
import { Icon } from '@components/ui-core/icon/icon'
import { Row } from '@components/ui-core/row/row'

import { HeroCard } from '@custom/components/hero-card/hero-card'

import { HomepageText } from '../text/homepage-text'

import styles from './chat.module.css'

type ChatProps = ComponentPropsWithRef<'div'>

const CHAT_MESSAGES = [
  {
    id: 0,
    message:
      "Looks like you're chatting with Antonio and Laura in the <span>#launch-project-chat</span>  and need to book a meeting. Would you like me to find a good time?",
  },
  {
    id: 1,
    message: 'yes!',
    isUser: true,
  },
  {
    id: 2,
    message: "You're all available during these times:",
    suggestions: [
      {
        id: 0,
        icon: (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="13"
            height="13"
            viewBox="0 0 13 13"
            fill="none"
          >
            <path
              d="M6.5 3.25V6.5H8.66667"
              stroke="currentColor"
              strokeWidth="1.08333"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M6.49967 11.9168C9.49122 11.9168 11.9163 9.4917 11.9163 6.50016C11.9163 3.50862 9.49122 1.0835 6.49967 1.0835C3.50813 1.0835 1.08301 3.50862 1.08301 6.50016C1.08301 9.4917 3.50813 11.9168 6.49967 11.9168Z"
              stroke="currentColor"
              strokeWidth="1.08333"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ),
        text: 'Monday at 3:00 PM',
      },
      {
        id: 1,
        icon: (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="13"
            height="13"
            viewBox="0 0 13 13"
            fill="none"
          >
            <path
              d="M6.5 3.25V6.5L7.58333 4.33333"
              stroke="currentColor"
              strokeWidth="1.08333"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M6.50065 11.9168C9.49219 11.9168 11.9173 9.4917 11.9173 6.50016C11.9173 3.50862 9.49219 1.0835 6.50065 1.0835C3.50911 1.0835 1.08398 3.50862 1.08398 6.50016C1.08398 9.4917 3.50911 11.9168 6.50065 11.9168Z"
              stroke="currentColor"
              strokeWidth="1.08333"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ),
        text: 'Tuesday at 1:00 PM',
      },
    ],
  },
]

const TYPING_MESSAGE = 'book it for monday'

const CHAT_CONTENT = {
  messages: CHAT_MESSAGES,
  typingMessage: TYPING_MESSAGE,
}

export function Chat(props: ChatProps): ReactElement {
  const { className, ...rest } = props
  const { messages, typingMessage } = CHAT_CONTENT

  return (
    <HeroCard className={clsx(styles.chat, className)} {...rest}>
      <Column gap="4x">
        <Icon variant={IconSprites.Hero} className={styles.heroIcon} />
        <Column gap="2x">
          {messages.map(message => (
            <HomepageText
              as="span"
              key={message.id}
              variant="heroCard"
              className={clsx(
                styles.message,
                message.isUser && styles.userMessage,
                message.suggestions && styles.hasSuggestions,
              )}
            >
              {parse(message.message)}
              {message.suggestions && (
                <Row crossAxisAlignment="center" gap="1x" className={styles.suggestions}>
                  {message.suggestions.map(suggestion => (
                    <Row key={suggestion.id} as="span" crossAxisAlignment="center" gap="1x">
                      {suggestion.icon}
                      {suggestion.text}
                    </Row>
                  ))}
                </Row>
              )}
            </HomepageText>
          ))}

          <div className={styles.textInput}>
            <Row crossAxisAlignment="end" gap="4x" mainAxisAlignment="spaceBetween">
              <HomepageText variant="heroCard">{typingMessage}</HomepageText>
              <Icon variant={IconSprites.Send} className={styles.sendIcon} />
            </Row>
          </div>
        </Column>
      </Column>
    </HeroCard>
  )
}
